/**
 * QC QR-tarama karar drawer'ı (mobil saha akışı).
 * Taranan lotun detayını (ürün + üretim: üretici/vardiya/hat + sipariş/müşteri) gösterir;
 * QC bekleyen lotlarda tek dokunuşla "Onayla (Serbest Bırak)" veya gerekçeli "Reddet → Karantina".
 * Aksiyonlar mevcut qc.releaseLot / qc.rejectLot uçlarını kullanır (aynı guard/audit).
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";

const STATUS_META: Record<string, { label: string; cls: string }> = {
  released: { label: "Serbest", cls: "b-success" },
  pending_qc: { label: "QC Bekliyor", cls: "b-warning" },
  on_hold: { label: "Blokeli", cls: "b-warning" },
  expired: { label: "SKT Geçti", cls: "b-danger" },
  rejected: { label: "Reddedildi", cls: "b-danger" },
  consumed: { label: "Tükendi", cls: "b-neutral" },
};

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}
function fmtDate(d: string | Date | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—";
}
function fmtDateTime(d: string | Date | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "short", timeStyle: "short" }).format(new Date(d)) : "—";
}

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: "1px solid var(--neutral-100)" }}>
      <span style={{ fontSize: 12.5, color: "var(--neutral-500)", flexShrink: 0 }}>{label}</span>
      <span className={mono ? "mono" : ""} style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", textAlign: "right", minWidth: 0 }}>{children}</span>
    </div>
  );
}

export function QcScanDrawer({
  lotId,
  onClose,
  onDone,
}: {
  lotId: string | null;
  onClose: () => void;
  /** Serbest/red sonrası — listeyi tazele + kapat. */
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const detailQ = trpc.qc.lotScanDetail.useQuery({ lotId: lotId ?? "" }, { enabled: !!lotId, staleTime: 0 });
  const d = detailQ.data;

  const releaseMut = trpc.qc.releaseLot.useMutation({ onSuccess: () => { qc.invalidateQueries(); onDone(); } });
  const rejectMut = trpc.qc.rejectLot.useMutation({ onSuccess: () => { qc.invalidateQueries(); onDone(); } });

  // lot değişince durumu sıfırla
  useEffect(() => { setRejecting(false); setReason(""); releaseMut.reset(); rejectMut.reset(); /* eslint-disable-next-line */ }, [lotId]);

  const busy = releaseMut.isPending || rejectMut.isPending;
  const sm = d ? STATUS_META[d.status] ?? { label: d.status, cls: "b-neutral" } : null;
  const orderTypeLabel = d?.order ? (d.order.orderType === "customer" ? "Müşteri siparişi" : "Stoğa üretim") : null;

  const footer = !d ? (
    <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
  ) : !d.canAct ? (
    <button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>
  ) : rejecting ? (
    <>
      <button type="button" className="btn-md ghost" onClick={() => setRejecting(false)} disabled={busy}>Vazgeç</button>
      <button type="button" className="btn-md danger" disabled={reason.trim().length === 0 || busy}
        onClick={() => d && rejectMut.mutate({ lotId: d.lotId, reason: reason.trim() })}>
        {rejectMut.isPending ? "İşleniyor…" : "Reddet → Karantina"}
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn-md danger-outline" disabled={busy} onClick={() => setRejecting(true)}>
        <Icon name="x" size={15} />Reddet
      </button>
      <button type="button" className="btn-md primary" disabled={busy} style={{ background: "var(--success)", borderColor: "var(--success)" }}
        onClick={() => d && releaseMut.mutate({ lotId: d.lotId })}>
        <Icon name="check" size={15} />{releaseMut.isPending ? "İşleniyor…" : "Onayla (Serbest Bırak)"}
      </button>
    </>
  );

  return (
    <Drawer open={!!lotId} onClose={onClose} size="sm" title="QC — QR Tarama"
      subtitle={d ? `${d.lotCode} · ${d.itemName}` : undefined} footer={footer}>
      {detailQ.isLoading ? (
        <div className="m-empty">Yükleniyor…</div>
      ) : detailQ.isError ? (
        <div className="form-banner-err">{detailQ.error.message}</div>
      ) : !d ? null : (
        <>
          {(releaseMut.error || rejectMut.error) ? (
            <div className="form-banner-err">{(releaseMut.error || rejectMut.error)?.message}</div>
          ) : null}

          {/* Başlık kartı: ürün + durum */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: "var(--neutral-900)" }}>{d.itemName}</span>
              <span className="mono" style={{ display: "block", fontSize: 12, color: "var(--neutral-500)" }}>{d.itemCode} · {d.lotCode}</span>
            </span>
            {sm ? <span className={`badge ${sm.cls} no-dot`}>{sm.label}</span> : null}
          </div>

          {!d.canAct ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", margin: "6px 0 10px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-600)", lineHeight: 1.5 }}>
              <Icon name="info" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Bu lot QC beklemede değil (durum: <b>{sm?.label}</b>). Serbest bırakma/red yalnız <b>QC bekleyen</b> veya <b>blokeli</b> lotlarda yapılır.</span>
            </div>
          ) : null}

          {/* Detay alanları */}
          <div style={{ marginTop: 4 }}>
            <Row label="Miktar" mono>{fmtQty(d.qtyOnHand)} {d.uom}</Row>
            <Row label="Üretim Tarihi" mono>{d.batch ? fmtDateTime(d.batch.producedAt) : fmtDate(d.producedAt)}</Row>
            {d.batch?.shiftName ? <Row label="Vardiya">{d.batch.shiftName}</Row> : null}
            {d.batch?.producerName ? <Row label="Üreten">{d.batch.producerName}</Row> : null}
            {d.batch?.lineName ? <Row label="Üretim Hattı">{d.batch.lineName}{d.batch.lineCode ? <span className="mono" style={{ color: "var(--neutral-400)", fontWeight: 400 }}> · {d.batch.lineCode}</span> : null}</Row> : null}
            {d.batch?.code ? <Row label="Üretim Partisi" mono>{d.batch.code}</Row> : null}
            {d.order ? <Row label="Sipariş" mono>{d.order.code}</Row> : null}
            {d.order?.customerName ? <Row label="Müşteri">{d.order.customerName}</Row> : null}
            {orderTypeLabel ? <Row label="Sipariş Tipi">{orderTypeLabel}</Row> : null}
            <Row label="SKT" mono>{fmtDate(d.expiresAt)}</Row>
            {d.supplierLot ? <Row label="Tedarikçi Lot" mono>{d.supplierLot}</Row> : null}
            {d.notes ? <Row label="Not">{d.notes}</Row> : null}
          </div>

          {/* Red gerekçesi (yalnız reddetme modunda) */}
          {rejecting ? (
            <div className="form-field" style={{ marginTop: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: "var(--neutral-50)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-700)", lineHeight: 1.5, marginBottom: 10 }}>
                <Icon name="alertTriangle" size={16} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
                <span><b className="mono">{d.lotCode}</b> reddedilip <b>karantinaya</b> alınacak (hurda edilmez). Eksik üretim aynı iş emrine eklenir.</span>
              </div>
              <label className="lbl" htmlFor="qc-scan-reason">Red sebebi <span style={{ color: "var(--danger)" }}>*</span></label>
              <textarea id="qc-scan-reason" className="form-textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} autoFocus
                placeholder="Örn. mikrobiyolojik uygunsuzluk, ambalaj hasarı…" />
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
